import configSource from "../../res/config.json";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { pdfLayoutsByCountry } from "../../logic/PdfPositions";
import {
  formatNumber,
  truncateText,
  capitalizeFirstLetter,
} from "../../logic/GeneralFunctions";

export async function GenerateFacturePDF(props) {
  const fileName = {
    fr: "facture_fictive.pdf",
    de: "fiktive_rechnung.pdf",
    uk: "mock_invoice.pdf",
    se: "fiktiv_faktura.pdf",
    no: "mock_invoice.pdf",
    dk: "mock_invoice.pdf",
    jp: "mock_invoice.pdf",
  };
  const lang = props.params.lang.toLowerCase();
  const formType = props.params.formType.toLowerCase();

  const response = await fetch(
    configSource.ROOT +
      `getimage?company=hcs&country=${lang}&type=${fileName[lang]}`,
  );
  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  const existingPdfBytes = await response.arrayBuffer();
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const pages = pdfDoc.getPages();
  const firstPage = pages[0];

  const layout = pdfLayoutsByCountry.facture[lang];

  const customerName =
    props.customerInformation.customerName ||
    props.customerInformation.customerFirstName +
      " " +
      props.customerInformation.customerLastName;

  firstPage.drawText(props.formInformation.contractNumber || "", {
    x: layout.contractNumber.x,
    y: layout.contractNumber.y,
    size: layout.contractNumber.size,
    font,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(capitalizeFirstLetter(customerName) || "", {
    x: layout.customerName.x,
    y: layout.customerName.y,
    size: layout.customerName.size,
    font,
    color: rgb(0, 0, 0),
  });

  let totalPriceExclVat = Object.values(props.productsValues).reduce(
    (sum, product) => {
      const productTotal = product.purchasePrice + product.installation;
      const accessoriesTotal = product.purchasePriceAccessorySum || 0;
      const totalPerProduct =
        (productTotal + accessoriesTotal) * product.quantity;
      return sum + totalPerProduct;
    },
    0,
  );

  if (formType === "upcare" && props.accessories) {
    Object.values(props.accessories).forEach((acc) => {
      if (acc.TotalAccessoriesPrice) {
        totalPriceExclVat += acc.TotalAccessoriesPrice;
      }
    });
  }

  const currencySymbol = props.labels.currencySymbol;
  const currencyAfterAmount = lang === "se" || lang === "no" || lang === "dk";

  const vatRate = 0.2;
  const totalPriceInclVat = totalPriceExclVat * (1 + vatRate);

  const productsLayout = layout.products;
  const startYProducts = productsLayout.startY;
  const rowHeightProducts = productsLayout.rowHeight;

  const noProductSerialText = {
    fr: "Ajouter le numéro de série",
    de: "Seriennummer hinzufügen",
    uk: "Add serial number",
    se: "Lägg till serienummer",
    no: "Legg til serienummer",
    dk: "Tilføj serienummer",
    jp: "Add serial number",
  };

  const accNoSerialText = {
    uk: "Not applicable",
    fr: "Sans objet",
    de: "Nicht zutreffend",
    se: "Ej tillämpligt",
    no: "Ikke relevant",
    dk: "Ikke relevant",
    jp: "Not applicable",
  };

  let currentRowIndex = 0;

  //Products
  Object.values(props.productsValues).forEach((product) => {
    const currentY = startYProducts - currentRowIndex * rowHeightProducts;

    const productQuantity = product.quantity.toString() || "";
    const productName = product.name || "";
    const productSerial =
      formType === "leaseplus" && product.name === props.labels.accessory
        ? accNoSerialText[lang]
        : product.serial[0] || noProductSerialText[lang];
    const productPrice =
      (product.purchasePrice + product.installation) * product.quantity || "";
    const serialFontSize =
      productSerial === "Ajouter le numéro de série" ||
      productSerial === "Seriennummer hinzufügen"
        ? 17
        : productsLayout.serialNumber.size;

    firstPage.drawText(productQuantity, {
      x: productsLayout.quantity.x,
      y: currentY,
      size: productsLayout.quantity.size,
      font,
      color: rgb(0, 0, 0),
    });

    firstPage.drawText(truncateText(productName, 50), {
      x: productsLayout.name.x,
      y: currentY,
      size: productsLayout.name.size,
      font,
      color: rgb(0, 0, 0),
    });

    firstPage.drawText(productSerial, {
      x: productsLayout.serialNumber.x,
      y: currentY,
      size: serialFontSize,
      font,
      color: rgb(0, 0, 0),
    });

    firstPage.drawText(formatNumber(productPrice, props.params.lang), {
      x: productsLayout.productPrice.x,
      y: currentY,
      size: productsLayout.productPrice.size,
      font,
      color: rgb(0, 0, 0),
    });

    currentRowIndex++;

    if (formType === "leaseplus") {
      if (product.accessory && product.accessory.length > 0) {
        product.accessory.forEach((accName, accIndex) => {
          if (!accName) return;

          const accNameDisplay = truncateText(accName, 49);
          const accessoryLayout = layout.accessories;
          const rowHeightAccessories = accessoryLayout.rowHeight;

          const accY = startYProducts - currentRowIndex * rowHeightAccessories;
          const accPrice =
            product.purchasePriceAccessory[accIndex] +
            product.installationAccessory[accIndex];

          firstPage.drawText(productQuantity, {
            x: accessoryLayout.quantity.x,
            y: accY,
            size: accessoryLayout.quantity.size,
            font,
            color: rgb(0, 0, 0),
          });

          firstPage.drawText(accNameDisplay, {
            x: accessoryLayout.name.x,
            y: accY,
            size: accessoryLayout.name.size,
            font,
            color: rgb(0, 0, 0),
          });

          firstPage.drawText(accNoSerialText[lang], {
            x: accessoryLayout.serialNumber.x,
            y: accY,
            size: accessoryLayout.serialNumber.size,
            font,
            color: rgb(0, 0, 0),
          });

          firstPage.drawText(formatNumber(accPrice, props.params.lang), {
            x: accessoryLayout.accessoryPrice.x,
            y: accY,
            size: accessoryLayout.accessoryPrice.size,
            font,
            color: rgb(0, 0, 0),
          });

          currentRowIndex++;
        });
      }
    }
  });

  if (formType === "upcare" && props.accessories) {
    const accessoryLayout = layout.accessories;

    Object.values(props.accessories).forEach((acc) => {
      if (!acc.AccessoriesName) return;
      const accY = startYProducts - currentRowIndex * accessoryLayout.rowHeight;

      firstPage.drawText(String(acc.AccessoriesQuantity || "1"), {
        x: accessoryLayout.quantity.x,
        y: accY,
        size: accessoryLayout.quantity.size,
        font,
        color: rgb(0, 0, 0),
      });

      firstPage.drawText(truncateText(acc.AccessoriesName, 50), {
        x: accessoryLayout.name.x,
        y: accY,
        size: accessoryLayout.name.size,
        font,
        color: rgb(0, 0, 0),
      });

      firstPage.drawText(accNoSerialText[lang], {
        x: accessoryLayout.serialNumber.x,
        y: accY,
        size: 20,
        font,
        color: rgb(0, 0, 0),
      });

      firstPage.drawText(formatNumber(acc.TotalAccessoriesPrice || 0, lang), {
        x: accessoryLayout.accessoryPrice.x,
        y: accY,
        size: accessoryLayout.accessoryPrice.size,
        font,
        color: rgb(0, 0, 0),
      });

      currentRowIndex++;
    });
  }

  firstPage.drawText(formatNumber(totalPriceExclVat, lang), {
    x: productsLayout.totalPriceExclVat.x,
    y: productsLayout.totalPriceExclVat.y,
    size: productsLayout.totalPriceExclVat.size,
    font,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText("20%", {
    x: productsLayout.vatRate.x,
    y: productsLayout.vatRate.y,
    size: productsLayout.vatRate.size,
    font,
    color: rgb(0, 0, 0),
  });

  firstPage.drawText(formatNumber(totalPriceInclVat, lang), {
    x: productsLayout.totalAmountIcnlVat.x,
    y: productsLayout.totalAmountIcnlVat.y,
    size: productsLayout.totalAmountIcnlVat.size,
    font,
    color: rgb(0, 0, 0),
  });
  const formattedNetAmount = currencyAfterAmount
    ? formatNumber(totalPriceInclVat, lang) + " " + currencySymbol
    : currencySymbol + formatNumber(totalPriceInclVat, lang);

  firstPage.drawText(formattedNetAmount, {
    x: productsLayout.netAmount.x,
    y: productsLayout.netAmount.y,
    size: productsLayout.netAmount.size,
    font,
    color: rgb(0, 0, 0),
  });
  const pdfBytes = await pdfDoc.save();
  return pdfBytes;
}
